import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const RankingQuestion = ({ question, rankedItems, onRankingChange }) => {
  const [items, setItems] = useState((rankedItems && rankedItems?.length > 0) ? rankedItems : question?.items);
  const [draggedItem, setDraggedItem] = useState(null);

  useEffect(() => {
    if (rankedItems && rankedItems?.length > 0) {
      setItems(rankedItems);
    } else if (question?.items) {
      setItems(question?.items);
    }
  }, [rankedItems, question?.items]);

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e?.preventDefault();
    if (draggedItem === null || draggedItem === index) return;

    const newItems = [...items];
    const draggedContent = newItems?.[draggedItem];
    newItems?.splice(draggedItem, 1);
    newItems?.splice(index, 0, draggedContent);

    setItems(newItems);
    setDraggedItem(index);
    onRankingChange(newItems);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  const moveItem = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items?.length) return;

    const newItems = [...items];
    [newItems[index], newItems[newIndex]] = [newItems?.[newIndex], newItems?.[index]];
    setItems(newItems);
    onRankingChange(newItems);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="space-y-2">
        <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
          {question?.text}
        </h2>
        <p className="text-sm md:text-base text-muted-foreground">
          {question?.instruction || "Glissez et déposez les éléments pour les classer par ordre de préférence"}
        </p>
      </div>
      <div className="space-y-3">
        {items?.map((item, index) => (
          <div
            key={item?.id}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
            className={`
              bg-card border-2 border-border rounded-xl p-4 md:p-5 transition-smooth cursor-move
              ${draggedItem === index ? 'opacity-50 scale-95' : 'hover:border-primary/50 hover:shadow-md'}
            `}
          >
            <div className="flex items-center gap-3 md:gap-4">
              <div className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <span className="text-base md:text-lg font-bold text-primary data-text">
                  {index + 1}
                </span>
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="text-sm md:text-base font-medium text-foreground">
                  {item?.text}
                </p>
              </div>

              <div className="flex-shrink-0 flex flex-col gap-1">
                <button
                  onClick={() => moveItem(index, 'up')}
                  disabled={index === 0}
                  className={`
                    w-8 h-8 rounded-lg flex items-center justify-center transition-smooth
                    ${index === 0 ? 'bg-muted text-muted-foreground cursor-not-allowed' : 'bg-muted hover:bg-primary hover:text-primary-foreground'}
                  `}
                  aria-label="Déplacer vers le haut"
                >
                  <Icon name="ChevronUp" size={16} />
                </button>
                <button
                  onClick={() => moveItem(index, 'down')}
                  disabled={index === items?.length - 1}
                  className={`
                    w-8 h-8 rounded-lg flex items-center justify-center transition-smooth
                    ${index === items?.length - 1 ? 'bg-muted text-muted-foreground cursor-not-allowed' : 'bg-muted hover:bg-primary hover:text-primary-foreground'}
                  `}
                  aria-label="Déplacer vers le bas"
                >
                  <Icon name="ChevronDown" size={16} />
                </button>
              </div>

              <div className="flex-shrink-0 text-muted-foreground">
                <Icon name="GripVertical" size={20} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RankingQuestion;